import React, { FormEvent } from 'react';
import Highcharts from 'highcharts';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import DisclosureWithSymbol from '@ecs/tng-common/components/disclosure-with-symbol/DisclosureWithSymbol';
import { addCommas, scrollTo } from '@ecs/tng-common/scripts/helpers';
import { TabPane, Tabs } from 'react-bootstrap';
// import { Link } from 'gatsby';
import { getAllParams, monthNames } from '../../helpers/functions';
import {
  calcMonthlyInterestRate,
  calcMonthlyPayment,
  CalculatorProps,
  drawHighChart,
  calcAPR
} from '../../helpers/calculators';
import { calcEducationalData } from '../../content/documents/disclosures';
import FormattedInput from '../FormattedInput';

type calculationTypes = {
  loanAmount: number;
  apr: number;
  numPayments: number;
  monthlyInterest: number;
  totalMonthlyPayment: number;
  totalAmountPaid: number;
  totalInterestPaid: number;
  payoffDate: string;
  principalPaidPercentage: number;
  interestPaidPercentage: number;
};
type APRCalcState = {
  loanAmount: number;
  interestRate: number;
  term: number;
  finCharges: number;
  calculations: calculationTypes;
  errors: {
    loanAmount: string;
    interestRate: string;
    term: string;
    finCharges: string;
  };
};
type inputTypes = 'loanAmount' | 'interestRate' | 'term' | 'finCharges';

const config: Record<inputTypes, any> & {
  title: string;
  tableTabTitle1: string;
  tableTabTitle2: string;
  highChartTitle: string;
  highChartLegendName1: string;
  highChartLegendName2: string;
  tableTh: string[];
} = {
  title: 'APR calculator',
  tableTabTitle1: 'Monthly payment',
  tableTabTitle2: 'Payment schedule',
  highChartTitle: 'Principal and interest',
  highChartLegendName1: 'Principal paid',
  highChartLegendName2: 'Interest paid',
  tableTh: ['#', 'Total payments', 'Total interest paid', 'Total principal paid', 'Balance'],
  term: {
    id: 'term',
    name: 'Term',
    type: 'tel',
    allowNegative: false,
    maxLength: 8,
    required: true,
    suffix: ' years',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue } = values;
      return floatValue === undefined || (floatValue <= 30 && floatValue >= 1);
    },
    allowLeadingZeros: false,
    errorMessages: {
      required: 'Please enter a valid term value'
    }
  },
  interestRate: {
    id: 'interestRate',
    name: 'Interest rate',
    type: 'tel',
    allowNegative: false,
    required: true,
    suffix: ' %',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue } = values;
      return floatValue === undefined || floatValue <= 30;
    },
    allowLeadingZeros: false,
    errorMessages: {
      required: 'Please enter a valid interest rate value',
      min: 'Interest rate must be greater than 0.1%.'
    }
  },
  loanAmount: {
    id: 'loanAmount',
    name: 'Loan amount',
    type: 'tel',
    allowNegative: false,
    maxLength: 10,
    required: true,
    thousandSeparator: ',',
    prefix: '$ ',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue } = values;
      return floatValue === undefined || (floatValue <= 1000000 && floatValue > 0);
    },
    allowLeadingZeros: false,
    errorMessages: {
      required: 'Please enter a valid loan amount value',
      min: 'Loan amount must be greater than $1000.'
    }
  },
  finCharges: {
    id: 'finCharges',
    name: 'Finance charges (optional)',
    type: 'tel',
    allowNegative: false,
    maxLength: 8,
    required: false,
    thousandSeparator: ',',
    prefix: '$ ',
    isAllowed: (values: NumberFormatValues) => {
      const { floatValue } = values;
      return floatValue === undefined || floatValue <= 200000;
    },
    allowLeadingZeros: false,
    errorMessages: {
      max: 'Value should be 20% of loan amount or less',
      required: ''
    }
  }
};

export default class APRCalc extends React.Component<CalculatorProps, APRCalcState> {
  constructor(props: CalculatorProps) {
    super(props);
    this.state = {
      loanAmount: 100000,
      interestRate: 6,
      term: 10,
      finCharges: 4000,
      calculations: {} as calculationTypes,
      errors: {
        loanAmount: '',
        interestRate: '',
        term: '',
        finCharges: ''
      }
    };
  }

  componentDidMount() {
    if (this.props.isFull && typeof window !== 'undefined') {
      const queryParams = getAllParams();
      if (queryParams) {
        const { loanAmount, interestRate, term, finCharges } = queryParams;
        this.setState(
          (prevState) => ({
            loanAmount: loanAmount ? Number(loanAmount) : prevState.loanAmount,
            interestRate: interestRate ? Number(interestRate) : prevState.interestRate,
            term: term ? Number(term) : prevState.term,
            finCharges: finCharges ? Number(finCharges) : prevState.finCharges
          }),
          () => {
            this.calculate(false);
          }
        );
      }
    }
    HighchartsAccessibility(Highcharts);
  }

  handleChange = (sourceInfo: SourceInfo, values: NumberFormatValues) => {
    const { event } = sourceInfo;
    if (!event) return;
    const target = event.target as HTMLInputElement;
    const name = target.name as inputTypes;
    const { errors } = this.state;
    let floatValue = Number(values?.floatValue);
    if (!config[name].required && !floatValue) {
      floatValue = 0;
    }
    // to set error messages
    const setError = (fieldName: inputTypes, message: string) => {
      errors[fieldName] = message;
    };
    // Validate based on the field name
    if (
      name === 'loanAmount' &&
      this.state.finCharges &&
      this.state.finCharges > floatValue * 0.2
    ) {
      setError('finCharges', config.finCharges.errorMessages.max);
    } else {
      setError('finCharges', ''); // Clear finCharges error
    }
    if (name === 'loanAmount' && floatValue < 1000) {
      setError(name, config.loanAmount.errorMessages.min);
    } else if (name === 'interestRate' && floatValue < 0.1) {
      setError(name, config.interestRate.errorMessages.min);
    } else if (name === 'finCharges' && floatValue > this.state.loanAmount * 0.2) {
      setError(name, config.finCharges.errorMessages.max);
    } else {
      const errorName =
        (!floatValue || Number.isNaN(floatValue) || floatValue === 0) && config[name].required
          ? config[name].errorMessages.required
          : '';
      setError(name, errorName);
    }
    // Update state with new values and errors
    this.setState({
      errors,
      [name]: floatValue
    } as unknown as Pick<APRCalcState, keyof APRCalcState>);
  };

  handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!this.checkIfFormIsInvalid()) {
      this.calculate(true);
    }
  };

  calculate(toScroll?: boolean) {
    const { loanAmount, interestRate, term, finCharges } = this.state;
    const numPayments = term * 12;
    const monthlyInterest = calcMonthlyInterestRate(interestRate);
    const totalMonthlyPayment = calcMonthlyPayment(loanAmount, monthlyInterest, numPayments);
    const totalAmountPaid = totalMonthlyPayment * numPayments;
    const totalInterestPaid = totalAmountPaid - loanAmount;
    const payoffDateObj = new Date();
    payoffDateObj.setMonth(payoffDateObj.getMonth() + numPayments);
    const payoffDate = monthNames[payoffDateObj.getMonth()] + ' ' + payoffDateObj.getFullYear();
    const apr = calcAPR(numPayments, monthlyInterest, loanAmount, finCharges);
    const principalPaidPercentage = parseFloat(
      (((totalAmountPaid - totalInterestPaid) / totalAmountPaid) * 100).toFixed(2)
    );
    const interestPaidPercentage = parseFloat(
      ((totalInterestPaid / totalAmountPaid) * 100).toFixed(2)
    );
    this.setState(
      {
        calculations: {
          loanAmount,
          apr,
          numPayments,
          monthlyInterest,
          totalMonthlyPayment,
          totalAmountPaid,
          totalInterestPaid,
          payoffDate,
          principalPaidPercentage,
          interestPaidPercentage
        }
      },
      () => {
        if (typeof toScroll === 'undefined' || toScroll) {
          scrollTo('calcTotals', -24);
        }
      }
    );
  }

  addQueryParams(): string {
    const query: { [key: string]: string } = {};
    const { term, loanAmount, interestRate, finCharges } = this.state;

    if (term) {
      query.term = term.toString();
    }
    if (loanAmount) {
      query.loanAmount = loanAmount.toString();
    }
    if (interestRate) {
      query.interestRate = interestRate.toString();
    }
    if (finCharges) {
      query.finCharges = finCharges.toString();
    }
    return new URLSearchParams(query).toString();
  }

  checkIfFormIsInvalid() {
    const { errors } = this.state;
    return Object.values(errors).some((error) => error.trim() !== '');
  }

  render() {
    const { isFull } = this.props;
    const { errors, calculations, loanAmount, term, finCharges, interestRate } = this.state;
    const renderForm = () => {
      return (
        <form
          className={`${calculations?.totalMonthlyPayment ? 'mb-4' : 'content-mb'} border rouded rounded-3 p-3 p-lg-4 ${!isFull ? 'rounded-top-0' : ''}`}
          onSubmit={this.handleSubmit}>
          <div
            className={`col-sm-10 col-md-8 ${isFull ? 'col-lg-12 col-xl-12' : 'col-lg-9 col-xl-8'}`}>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.loanAmount}
                value={loanAmount?.toString()}
                errors={errors.loanAmount}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={loanAmount?.toString()}
              />
              {errors.loanAmount && <div className="invalid-feedback">{errors.loanAmount}</div>}
            </div>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.finCharges}
                value={finCharges?.toString()}
                errors={errors.finCharges}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={finCharges?.toString()}
              />
              {errors.finCharges && <div className="invalid-feedback">{errors.finCharges}</div>}
            </div>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.interestRate}
                value={interestRate?.toString()}
                errors={errors.interestRate}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={interestRate?.toString()}
              />
              {errors.interestRate && <div className="invalid-feedback">{errors.interestRate}</div>}
            </div>
            <div className="mb-3 form-floating">
              <FormattedInput
                config={config.term}
                value={term?.toString()}
                errors={errors.term}
                onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                  this.handleChange(sourceInfo, values)
                }
                placeholder={term?.toString()}
              />
              {errors.term && <div className="invalid-feedback">{errors.term}</div>}
            </div>
            <button
              disabled={this.checkIfFormIsInvalid()}
              type="submit"
              className="btn btn-secondary mt-3">
              Calculate
            </button>
          </div>
        </form>
      );
    };
    const renderTable = () => {
      const paymentsArray = Array.from(
        { length: calculations.numPayments },
        (_, index) => index + 1
      );

      let balance = calculations.loanAmount;
      let totalInterestPaidCalc = 0;
      let totalPrincipalPaid = 0;
      return (
        <div
          className={`${calculations.numPayments > 11 ? 'table-vertical-scroll' : ''} table-horizontal-scroll-sm`}>
          <table className="table table-striped">
            <thead>
              <tr>
                {config.tableTh.map((th) => (
                  <th key={th}>{th}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paymentsArray.map((paymentNumber) => {
                const interestPaid = balance * calculations.monthlyInterest;
                const principalPaid = calculations.totalMonthlyPayment - interestPaid;
                totalInterestPaidCalc += interestPaid;
                totalPrincipalPaid += principalPaid;
                balance -= principalPaid;

                return (
                  <tr key={paymentNumber}>
                    <td>{paymentNumber}</td>
                    <td>${addCommas((totalInterestPaidCalc + totalPrincipalPaid).toFixed(0))}</td>
                    <td>${addCommas(totalInterestPaidCalc.toFixed(0))}</td>
                    <td>${addCommas(totalPrincipalPaid.toFixed(0))}</td>
                    <td>${addCommas(Math.abs(balance).toFixed(0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };
    const renderTotals = () => {
      return (
        <div className={`row gx-md-6 ${isFull ? 'pt-4 gy-4' : 'pt-3 pt-lg-4 gy-3 gy-lg-4'}`}>
          <div className="col-md-6 col-lg-12 col-xl-6">
            <div className="bg-light rounded p-4 d-flex flex-wrap text-center mb-5">
              <span className="text-gray w-100">Annual Percentage Rate (APR)</span>
              <span className="h2 mb-0 w-100">{calculations.apr.toFixed(2)}%</span>
            </div>
            <div className="border-bottom d-flex justify-content-between pb-2 pb-lg-3">
              <span className="text-gray">
                Estimated monthly payment<sup>†</sup>:
              </span>
              <span className="fw-bold">
                ${addCommas(calculations.totalMonthlyPayment.toFixed(2))}
              </span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 pb-lg-3">
              <span className="text-gray">Number of payments:</span>
              <span className="fw-bold">{calculations.numPayments}</span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 pb-lg-3">
              <span className="text-gray">Payoff date:</span>
              <span className="fw-bold">{calculations.payoffDate}</span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 pb-lg-3">
              <span className="text-gray">Total interest paid:</span>
              <span className="fw-bold">
                ${addCommas(calculations.totalInterestPaid.toFixed(2))}
              </span>
            </div>
            <div className="d-flex justify-content-between pt-2">
              <span className="text-gray">Total payments:</span>
              <span className="fw-bold">${addCommas(calculations.totalAmountPaid.toFixed(2))}</span>
            </div>
          </div>
          {drawHighChart({
            title: config.highChartTitle,
            option1: {
              name: config.highChartLegendName1,
              value: calculations.principalPaidPercentage
            },
            option2: {
              name: config.highChartLegendName2,
              value: calculations.interestPaidPercentage
            }
          })}
        </div>
      );
    };
    const renderCalculationsBox = () => {
      return Object.keys(calculations).length > 0 ? (
        <>
          <div
            id="calcTotals"
            className="result border rounded-3 overflow-hidden body-text-sm">
            <div className={`px-3 pb-3 pb-lg-4 px-lg-4 ${isFull ? 'pt-3 pt-lg-4' : ''}`}>
              {isFull ? (
                <Tabs
                  id="calc-tab"
                  defaultActiveKey="calcTab1"
                  className="nav-underline">
                  <TabPane
                    eventKey="calcTab1"
                    title={config.tableTabTitle1}>
                    {renderTotals()}
                  </TabPane>
                  <TabPane
                    eventKey="calcTab2"
                    title={config.tableTabTitle2}>
                    {renderTable()}
                  </TabPane>
                </Tabs>
              ) : (
                renderTotals()
              )}
            </div>
            <div className="bg-light p-3 p-lg-4 disclosures text-gray">
              <DisclosureWithSymbol disclosure={calcEducationalData} />
            </div>
          </div>
          {/* // REPLACE WITH GATSBY LINK */}
          {!isFull && (
            <p className="body-text-sm mt-3 d-flex align-items-center mb-6 mb-lg-7">
              <span className="ecs-font-icon-con-calculator font-icon-size-md" /> Try the full&nbsp;
              <a href={`/blogs/ask-experian/apr-calculator/?${this.addQueryParams()}`}>
                APR calculator
              </a>
              &nbsp;with more features.
            </p>
          )}
        </>
      ) : null;
    };
    return isFull ? (
      <div className="d-lg-flex row content-mt">
        <div className="col-lg-5 col-xl-4">{renderForm()}</div>
        <div className="col-lg-7 col-xl-8">{renderCalculationsBox()}</div>
      </div>
    ) : (
      <>
        <h4 className="calc-title mb-0 rounded-3 rounded-bottom-0 p-3 py-lg-4 px-lg-5 bg-midnight-blue text-white content-mt">
          {config.title}
        </h4>
        {renderForm()}
        {renderCalculationsBox()}
      </>
    );
  }
}
